import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLocale, useT } from '@/i18n';
import { useSession } from '@/hooks/session';
import { AppShell } from '@/components/layout';
import { Button, Check, Illustration } from '@/components/ui';
import { cx } from '@/components/ui/cx';
import { RichText } from '@/components/trust/RichText';
import { CrisisNote } from '@/components/trust/CrisisNote';
import { type LearnGroup, type LearnMeta } from '@/content/learn';
import { ARTICLES } from '@/content/learn/articles';
import { ARTICLES_HI } from '@/content/learn/articles.hi';
import { learnFor, learnMetaFor } from '@/content/learn/localized';
import { photo } from '@/lib/photos';
import { NotFound } from './NotFound';

const GROUPS: LearnGroup[] = ['conditions', 'situations', 'guides'];

const Meta = ({ a }: { a: LearnMeta }) => {
  const t = useT();
  return (
    <span className="text-caption text-ink-muted">
      {a.ages} · {t('learn.minutes', { n: a.minutes })}
    </span>
  );
};

/**
 * The three Learn groups as tabs that stay under the header while the page scrolls, so any group is
 * one tap away from anywhere. On the index they jump in place and follow the section in view; on an
 * article they lead back to the index, with the article's own group marked.
 */
const LearnTabs = ({ onIndex, current }: { onIndex: boolean; current?: LearnGroup }) => {
  const t = useT();
  const [inView, setInView] = useState<LearnGroup | undefined>(current);

  useEffect(() => {
    if (!onIndex || typeof IntersectionObserver === 'undefined') return;
    // Entries carry only the sections that changed, so keep the full visible set and mark the first.
    const visible = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => (e.isIntersecting ? visible.add(e.target.id) : visible.delete(e.target.id)));
        const first = GROUPS.find((g) => visible.has(g));
        if (first) setInView(first);
      },
      { rootMargin: '-140px 0px -55% 0px' },
    );
    GROUPS.forEach((g) => {
      const el = document.getElementById(g);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [onIndex]);

  const active = onIndex ? inView : current;
  return (
    <nav aria-label={t('learn.jump')} className="sticky top-[68px] z-20 border-b border-line bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/85">
      <ul className="mx-auto flex max-w-content gap-2 overflow-x-auto px-4 py-2.5 md:px-6">
        {GROUPS.map((g) => {
          const className = cx(
            'inline-flex min-h-tap shrink-0 items-center whitespace-nowrap rounded-pill border-[1.5px] px-4 text-label transition-colors duration-fade',
            active === g ? 'border-lagoon-600 bg-lagoon-600 text-white' : 'border-sage-300 bg-white text-ink hover:border-lagoon-500 hover:text-lagoon-700',
          );
          return (
            <li key={g}>
              {onIndex ? (
                <a href={`#${g}`} aria-current={active === g ? 'location' : undefined} className={className}>
                  {t(`learn.group.${g}`)}
                </a>
              ) : (
                <Link to={`/learn#${g}`} aria-current={active === g ? 'page' : undefined} className={className}>
                  {t(`learn.group.${g}`)}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

/** Condition pages: picture beside the words, two per row — the richest cards on the page. */
const ConditionCard = ({ a }: { a: LearnMeta }) => (
  <li>
    <Link to={`/learn/${a.slug}`} className="lift group grid h-full grid-cols-[112px_1fr] overflow-hidden rounded-lg bg-white shadow-1 sm:grid-cols-[148px_1fr]">
      <Illustration src={photo(a.photo)} alt="" className="h-full min-h-[132px]" position="50% 35%" />
      <span className="flex flex-col gap-1 p-4">
        <span className="text-h3 text-ink group-hover:text-lagoon-700">{a.title}</span>
        <span className="line-clamp-3 text-body text-ink-soft">{a.summary}</span>
        <span className="mt-auto pt-1">
          <Meta a={a} />
        </span>
      </span>
    </Link>
  </li>
);

/** Everyday situations: compact rows, quick to scan. */
const SituationRow = ({ a }: { a: LearnMeta }) => (
  <li>
    <Link to={`/learn/${a.slug}`} className="group grid grid-cols-[1fr_auto] items-center gap-4 border-b border-line py-4 hover:bg-white/60">
      <span>
        <span className="block text-label text-ink group-hover:text-lagoon-700">{a.title}</span>
        <span className="block text-body text-ink-soft">{a.summary}</span>
      </span>
      <span aria-hidden="true" className="text-lagoon-600 transition-transform duration-fade group-hover:translate-x-1">→</span>
    </Link>
  </li>
);

/** Guides: numbered, like chapters. */
const GuideItem = ({ a, n }: { a: LearnMeta; n: number }) => (
  <li>
    <Link to={`/learn/${a.slug}`} className="group flex h-full flex-col rounded-lg border-t-[3px] border-sage-300 bg-white p-5 hover:border-lagoon-500">
      <span className="font-display text-[15px] font-extrabold tabular-nums text-sage-500">{String(n).padStart(2, '0')}</span>
      <span className="mt-1 text-h3 text-ink group-hover:text-lagoon-700">{a.title}</span>
      <span className="mt-1 text-body text-ink-soft">{a.summary}</span>
    </Link>
  </li>
);

export const LearnIndex = () => {
  const t = useT();
  const { locale } = useLocale();
  const by = (g: LearnGroup) => learnFor(locale).filter((a) => a.group === g);

  return (
    <AppShell wide>
      <div className="mx-auto grid max-w-content items-center gap-10 px-4 pb-12 pt-10 md:px-6 nav:grid-cols-[1.2fr_1fr] nav:pt-14">
        <div>
          <p className="eyebrow text-lagoon-600">{t('learn.eyebrow')}</p>
          <h1 className="mt-3 text-hero">{t('learn.title')}</h1>
          <p className="mt-5 max-w-[34em] text-[18px] leading-relaxed text-ink-soft">{t('learn.sub')}</p>
        </div>
        <div aria-hidden="true" className="mx-auto hidden h-[340px] w-full max-w-[460px] overflow-hidden rounded-xl nav:block">
          <Illustration src={photo('learn-hero')} alt="" position="50% 40%" eager />
        </div>
      </div>

      <LearnTabs onIndex />

      <section id="conditions" aria-labelledby="conditions-h" className="mx-auto max-w-content scroll-mt-36 px-4 py-10 md:px-6">
        <h2 id="conditions-h" className="text-display">{t('learn.group.conditions')}</h2>
        <p className="mt-2 max-w-[40em] text-body text-ink-soft">{t('learn.group.conditions.body')}</p>
        <ul className="mt-6 grid gap-4 md:grid-cols-2">{by('conditions').map((a) => <ConditionCard key={a.slug} a={a} />)}</ul>
      </section>

      <section id="situations" aria-labelledby="situations-h" className="scroll-mt-36 bg-white/70">
        <div className="mx-auto grid max-w-content gap-8 px-4 py-14 md:px-6 nav:grid-cols-[1fr_2fr]">
          <div>
            <h2 id="situations-h" className="text-display">{t('learn.group.situations')}</h2>
            <p className="mt-2 text-body text-ink-soft">{t('learn.group.situations.body')}</p>
          </div>
          <ul className="border-t border-line">{by('situations').map((a) => <SituationRow key={a.slug} a={a} />)}</ul>
        </div>
      </section>

      <section id="guides" aria-labelledby="guides-h" className="mx-auto max-w-content scroll-mt-36 px-4 py-14 md:px-6">
        <h2 id="guides-h" className="text-display">{t('learn.group.guides')}</h2>
        <p className="mt-2 max-w-[40em] text-body text-ink-soft">{t('learn.group.guides.body')}</p>
        <ol className="mt-6 grid gap-4 sm:grid-cols-2 nav:grid-cols-4">{by('guides').map((a, i) => <GuideItem key={a.slug} a={a} n={i + 1} />)}</ol>
      </section>

      <p className="mx-auto max-w-content px-4 pb-14 text-caption text-ink-muted md:px-6">{t('learn.disclaimer')}</p>
    </AppShell>
  );
};

export const LearnArticle = () => {
  const t = useT();
  const { locale } = useLocale();
  const { account } = useSession();
  const { slug = '' } = useParams();
  const meta = learnMetaFor(slug, locale);
  const body = (locale === 'hi' ? ARTICLES_HI[slug] : undefined) ?? ARTICLES[slug];
  if (!meta || !body) return <NotFound />;
  const related = learnFor(locale).filter((a) => a.slug !== slug && (a.group === meta.group || a.conditions?.some((c) => meta.conditions?.includes(c)))).slice(0, 3);
  const cta = account ? '/children' : '/signup';

  return (
    <AppShell wide>
      <LearnTabs onIndex={false} current={meta.group} />
      <div className="mx-auto max-w-content px-4 py-6 md:px-6 md:py-8">
      <nav aria-label={t('learn.breadcrumb')} className="text-caption">
        <Link to="/learn" className="font-bold text-lagoon-600 hover:underline">
          {t('nav.learn')}
        </Link>
        <span aria-hidden="true" className="px-2 text-ink-muted">/</span>
        <Link to={`/learn#${meta.group}`} className="font-bold text-lagoon-600 hover:underline">
          {t(`learn.group.${meta.group}`)}
        </Link>
      </nav>

      <div className="mt-4 grid gap-10 nav:grid-cols-[minmax(0,1fr)_320px] nav:gap-14">
        <article className="min-w-0 max-w-prose">
          <h1 className="text-display">{meta.title}</h1>
          <p className="mt-2">
            <Meta a={meta} />
          </p>
          <div className="mt-6 aspect-[3/2] overflow-hidden rounded-xl">
            <Illustration src={photo(meta.photo)} alt="" position="50% 40%" eager />
          </div>

          <div className="mt-8 grid gap-4 text-[17px] leading-relaxed text-ink">
            {body.intro.map((p, i) => (
              <p key={i}>
                <RichText text={p} />
              </p>
            ))}
          </div>

          {body.notice && (
            <section aria-labelledby="notice" className="mt-10">
              <h2 id="notice">{t('learn.notice')}</h2>
              <ul className="mt-4 grid gap-3">
                {body.notice.map((n, i) => (
                  <li key={i} className="grid grid-cols-[10px_1fr] gap-3 text-body text-ink">
                    <span aria-hidden="true" className="mt-2 h-2.5 w-2.5 rounded-full bg-sage-500" />
                    <span>
                      <RichText text={n} />
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {body.helps && (
            <section aria-labelledby="helps" className="mt-10 rounded-xl bg-mint-50 p-6">
              <h2 id="helps">{t('learn.helps')}</h2>
              <ul className="mt-4 grid gap-3">
                {body.helps.map((h, i) => (
                  <li key={i} className="grid grid-cols-[24px_1fr] gap-3 text-body text-ink">
                    <Check className="mt-0.5 h-5 w-5 text-progress-ink" />
                    <span>
                      <RichText text={h} />
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {body.extra?.map((s) => (
            <section key={s.heading} className="mt-10">
              <h2>{s.heading}</h2>
              <div className="mt-4 grid gap-4 text-body leading-relaxed text-ink">
                {s.blocks.map((b, i) =>
                  typeof b === 'string' ? (
                    <p key={i}>
                      <RichText text={b} />
                    </p>
                  ) : (
                    <ul key={i} className="grid gap-2 pl-5 [list-style:disc] marker:text-sage-500">
                      {b.map((item, k) => (
                        <li key={k} className="pl-1">
                          <RichText text={item} />
                        </li>
                      ))}
                    </ul>
                  ),
                )}
              </div>
            </section>
          ))}

          {body.myths && (
            <section aria-labelledby="myths" className="mt-10">
              <h2 id="myths">{t('learn.myths')}</h2>
              <ul className="mt-4 grid gap-3">
                {body.myths.map((m, i) => (
                  <li key={i} className="grid overflow-hidden rounded-lg border border-line bg-white sm:grid-cols-2">
                    <p className="bg-paper px-5 py-4 text-body text-ink-soft">
                      <span className="eyebrow block text-ink-muted">{t('learn.myth')}</span>
                      {m.myth}
                    </p>
                    <p className="px-5 py-4 text-body text-ink">
                      <span className="eyebrow block text-lagoon-600">{t('learn.fact')}</span>
                      {m.fact}
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {body.when && (
            <section aria-labelledby="when" className="mt-10 rounded-xl border-l-4 border-provider bg-provider-soft p-6">
              <h2 id="when" className="text-provider-ink">{t('learn.when')}</h2>
              <ul className="mt-3 grid gap-2 pl-5 text-body text-ink [list-style:disc]">
                {body.when.map((w, i) => (
                  <li key={i}>
                    <RichText text={w} />
                  </li>
                ))}
              </ul>
              {body.who && (
                <>
                  <p className="mt-5 text-label text-provider-ink">{t('learn.who')}</p>
                  <ul className="mt-2 flex flex-wrap gap-2">
                    {body.who.map((w) => (
                      <li key={w} className="rounded-pill bg-white px-3 py-1.5 text-caption text-ink">
                        {w}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </section>
          )}

          <section aria-labelledby="sources" className="mt-12 border-t border-line pt-6">
            <h2 id="sources" className="text-h3">{t('learn.sources')}</h2>
            <ul className="mt-3 grid gap-1.5 text-label">
              {body.sources.map((s) => (
                <li key={s.href}>
                  <a href={s.href} target="_blank" rel="noopener noreferrer" className="text-lagoon-700 underline decoration-lagoon-200 underline-offset-4 hover:decoration-lagoon-700">
                    {s.label}
                  </a>
                </li>
              ))}
            </ul>
            <p className="mt-5 text-caption text-ink-muted">
              {meta.reviewedBy ? t('learn.reviewed', { name: meta.reviewedBy, date: meta.updated }) : t('learn.updated', { date: meta.updated })} · {t('learn.disclaimer')}
            </p>
          </section>
        </article>

        <aside className="flex flex-col gap-5 nav:sticky nav:top-24 nav:self-start">
          <div className="rounded-xl bg-lagoon-600 p-6 text-white shadow-2">
            <p className="eyebrow text-coral-300">{t('learn.nurture.eyebrow')}</p>
            <p className="mt-2 text-body text-lagoon-100">
              <RichText text={body.nurture} />
            </p>
            <Button as="a" href={cta} variant="celebrate" full className="mt-5">
              {t('landing.cta')}
            </Button>
          </div>
          {related.length > 0 && (
            <nav aria-label={t('learn.related')} className="rounded-lg bg-white p-5 shadow-1">
              <p className="text-label text-ink">{t('learn.related')}</p>
              <ul className="mt-2 grid">
                {related.map((a) => (
                  <li key={a.slug}>
                    <Link to={`/learn/${a.slug}`} className="flex min-h-tap items-center justify-between gap-3 border-b border-line py-2 text-label text-lagoon-700 last:border-0 hover:underline">
                      {a.title}
                      <span aria-hidden="true">→</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          )}
          <div className="rounded-lg bg-coral-100 p-5">
            <CrisisNote className="text-ink" />
          </div>
        </aside>
      </div>
      </div>
    </AppShell>
  );
};

export default LearnIndex;
